import { Button, MagellanPopup } from 'components/atoms';
import { FormInput } from 'components/molecules';
import { Formik } from 'formik';
import { useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { PiFileCsvDuotone, PiNotepadBold } from 'react-icons/pi';
import Popup from 'reactjs-popup';
import { PopupActions } from 'reactjs-popup/dist/types';
import { useGetMagellanAccountsLightList } from 'services/accounts';
import { useGetPredefindedSalesChannels, useGetStatementTemplates } from 'services/enums';
import { useGetSalesChannelProviders } from 'services/salesChannel';
import { useGetSettlementLightList } from 'services/settlementPeriods';
import { usePostStatementChunkedUpload } from 'services/statements';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { getSettlementPeriodName } from 'utils/global/name.utils';
import { object, string } from 'yup';

interface IStatementUploadFormValues {
  magellanAccountId: string;
  salesChannelId: string;
  providerId: string;
  settlementPeriodId: string;
  templateType: string;
}

interface IPopupStatementUploadProps {
  trigger?: JSX.Element;
  defaultMagellanAccountId?: string;
  onSuccess?: () => void;
}

const initialValues: IStatementUploadFormValues = {
  magellanAccountId: '',
  salesChannelId: '',
  providerId: '',
  settlementPeriodId: '',
  templateType: '',
};

const validator = object().shape({
  magellanAccountId: string().required(),
  salesChannelId: string().required(),
  providerId: string().required(),
  settlementPeriodId: string().required(),
  templateType: string().required(),
});

const PopupStatementUpload = ({
  trigger,
  defaultMagellanAccountId = '',
  onSuccess,
}: IPopupStatementUploadProps) => {
  const popupRef = useRef<PopupActions>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const { t } = useTranslation();

  const accounts = useGetMagellanAccountsLightList();
  const templateTypes = useGetStatementTemplates();
  const importMutation = usePostStatementChunkedUpload();

  const accountsOptions = useMemo(
    () =>
      accounts.data?.map(account => ({
        value: account.id,
        label: account.accountName,
      })) ?? [],
    [accounts.data]
  );

  const templateOptions = useMemo(
    () =>
      templateTypes.data?.map(template => ({
        value: String(template.id),
        label: template.name,
      })) ?? [],
    [templateTypes.data]
  );

  const onClose = () => {
    setFile(null);
    setUploadProgress(0);
    importMutation.resetProgress();
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <Popup ref={popupRef} modal nested trigger={trigger} onClose={onClose}>
      <MagellanPopup className='p-5 rounded-md'>
        <MagellanPopup.Header className='justify-center w-80 mb-5'>
          <PiNotepadBold /> {t('statements.popup.header')}
        </MagellanPopup.Header>

        <Formik<IStatementUploadFormValues>
          enableReinitialize
          initialValues={{
            ...initialValues,
            magellanAccountId: defaultMagellanAccountId,
          }}
          validationSchema={validator}
          onSubmit={async values => {
            if (!file) {
              toast.error(t('statements.popup.fileRequired'));
              return;
            }

            const selectedTemplate = templateTypes.data?.find(
              template => String(template.id) === values.templateType
            );
            const allowed = selectedTemplate?.allowedExtension?.replace('.', '');
            if (allowed) {
              const ext = file.name.split('.').pop()?.toLowerCase();
              if (ext !== allowed.toLowerCase()) {
                toast.error(
                  t('formik-error.invalid-file-extension-template', {
                    extension: allowed,
                    templateName: selectedTemplate?.name ?? '',
                  })
                );
                return;
              }
            }

            setUploadProgress(0);
            await importMutation.mutateAsync(
              {
                magellanAccountId: values.magellanAccountId,
                salesChannelId: values.salesChannelId,
                providerId: values.providerId,
                settlementPeriodId: values.settlementPeriodId,
                templateType: Number(values.templateType),
                file,
                onProgress: setUploadProgress,
              },
              {
                onSuccess: () => {
                  popupRef.current?.close();
                  onSuccess?.();
                },
              }
            );
          }}>
          {({ submitForm, values, isValid, setFieldValue }) => (
            <PopupContent
              accountId={values.magellanAccountId}
              salesChannelId={values.salesChannelId}
              accountsOptions={accountsOptions}
              templateOptions={templateOptions}
              file={file}
              setFile={setFile}
              fileInputRef={fileInputRef}
              isPending={importMutation.isPending}
              uploadProgress={uploadProgress}
              disabled={
                !isValid ||
                !file ||
                !values.magellanAccountId ||
                !values.salesChannelId ||
                !values.providerId ||
                !values.settlementPeriodId ||
                !values.templateType
              }
              onAdd={submitForm}
              onAccountChange={() => {
                setFieldValue('salesChannelId', '');
                setFieldValue('providerId', '');
                setFieldValue('settlementPeriodId', '');
              }}
              onSalesChannelChange={() => {
                setFieldValue('providerId', '');
              }}
            />
          )}
        </Formik>
      </MagellanPopup>
    </Popup>
  );
};

interface IPopupContentProps {
  accountId: string;
  salesChannelId: string;
  accountsOptions: { value: string; label: string }[];
  templateOptions: { value: string; label: string }[];
  file: File | null;
  setFile: (file: File | null) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  isPending: boolean;
  uploadProgress: number;
  disabled: boolean;
  onAdd: () => void;
  onAccountChange: () => void;
  onSalesChannelChange: () => void;
}

const PopupContent = ({
  accountId,
  salesChannelId,
  accountsOptions,
  templateOptions,
  file,
  setFile,
  fileInputRef,
  isPending,
  uploadProgress,
  disabled,
  onAdd,
  onAccountChange,
  onSalesChannelChange,
}: IPopupContentProps) => {
  const { t } = useTranslation();

  const salesChannels = useGetPredefindedSalesChannels(
    accountId,
    !!accountId
  );
  const providers = useGetSalesChannelProviders(
    salesChannelId,
    accountId,
    !!accountId && !!salesChannelId
  );
  const settlementPeriods = useGetSettlementLightList(
    accountId,
    !!accountId
  );

  const salesChannelOptions = useMemo(
    () =>
      salesChannels.data?.map(channel => ({
        value: String(channel.id),
        label: channel.name,
      })) ?? [],
    [salesChannels.data]
  );

  const providerOptions = useMemo(
    () =>
      providers.data?.map(provider => ({
        value: provider.id,
        label: provider.name,
      })) ?? [],
    [providers.data]
  );

  const settlementPeriodOptions =
    settlementPeriods.data?.map(period => ({
      value: period.id,
      label: getSettlementPeriodName(period.settlementPeriodName) ?? '',
    })) ?? [];

  return (
    <div className='flex flex-col gap-3 w-80'>
      <FormInput.Select
        name='magellanAccountId'
        label={t('input.label.magellanAccount')}
        options={accountsOptions}
        autoFocus
        onChange={() => onAccountChange()}
      />

      <FormInput.Select
        name='salesChannelId'
        label={t('input.label.salesChannel')}
        options={salesChannelOptions}
        isDisabled={!accountId}
        onChange={() => onSalesChannelChange()}
      />

      <FormInput.Select
        name='providerId'
        label={t('input.label.provider')}
        options={providerOptions}
        isDisabled={!accountId || !salesChannelId}
      />

      <FormInput.Select
        name='settlementPeriodId'
        label={t('input.label.settlementPeriod')}
        options={settlementPeriodOptions}
        isDisabled={!accountId}
      />

      <FormInput.Select
        name='templateType'
        label={t('input.label.templateType')}
        options={templateOptions}
      />

      <input
        ref={fileInputRef}
        type='file'
        className='hidden'
        onChange={event => {
          const next = event.target.files?.[0] ?? null;
          setFile(next);
        }}
      />

      <Button
        type='button'
        fullWidth
        onClick={() => fileInputRef.current?.click()}>
        <Button.Icon icon={<PiFileCsvDuotone />} />
        <Button.Text text={file ? file.name : t('button.selectFile')} />
      </Button>

      {file && (
        <p className='text-xs text-gray-500'>
          {t('statements.popup.file')}: {file.name}
        </p>
      )}

      {isPending && (
        <div className='flex flex-col gap-1'>
          <div className='h-2 w-full rounded bg-gray-200 overflow-hidden'>
            <div
              className='h-full bg-blue-500 transition-all'
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
          <p className='text-xs text-gray-500 text-right'>{uploadProgress}%</p>
        </div>
      )}

      <MagellanPopup.BottomButtons>
        <Button
          type='button'
          variant={BUTTON_VARIANT.submit}
          disabled={disabled}
          isLoading={isPending}
          onClick={onAdd}>
          <Button.Text text={t('button.upload')} />
          <Button.Icon icon={<PiNotepadBold />} />
        </Button>
      </MagellanPopup.BottomButtons>
    </div>
  );
};

export default PopupStatementUpload;
